import { TrendingUp, TrendingDown, Activity } from "lucide-react";
import {
  Bar,
  BarChart,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fromKm, fmt, unitLabel } from "@/lib/units";
import { colorForVehicle } from "@/lib/vehicleColors";
import { useThemeColors } from "@/lib/theme";

/**
 * Classify one vehicle's current-month performance vs its own history.
 * Returns {trend, label, deltaPct, projectedMonthEndKm, historicalAvgKm}.
 *
 *   above   : projected month-end > 115% of historical average
 *   below   : projected month-end < 85% of historical average
 *   in_line : within that band
 *   unknown : no past months yet
 */
function classifyVehicle({ vStats, dayOfMonth, daysInMonth }) {
  const monthUsedKm = vStats?.month_used_km || 0;
  const monthStart = vStats?.month_start;
  const currentMonthKey =
    monthStart ? monthStart.slice(0, 7) : new Date().toISOString().slice(0, 7);
  const pastMonths = (vStats?.monthly || []).filter(
    (m) => m.month && m.month < currentMonthKey && (m.distance_km || 0) > 0,
  );
  const historicalAvgKm =
    pastMonths.length > 0
      ? pastMonths.reduce((acc, m) => acc + (m.distance_km || 0), 0) / pastMonths.length
      : 0;
  const monthDailyRate = dayOfMonth > 0 ? monthUsedKm / dayOfMonth : 0;
  const projectedMonthEndKm = monthDailyRate * daysInMonth;
  let trend = "unknown";
  let deltaPct = null;
  if (historicalAvgKm > 0) {
    deltaPct = ((projectedMonthEndKm - historicalAvgKm) / historicalAvgKm) * 100;
    if (projectedMonthEndKm > historicalAvgKm * 1.15) trend = "above";
    else if (projectedMonthEndKm < historicalAvgKm * 0.85) trend = "below";
    else trend = "in_line";
  }
  return {
    trend,
    deltaPct,
    monthUsedKm,
    projectedMonthEndKm,
    historicalAvgKm,
    pastMonthCount: pastMonths.length,
  };
}

function trendStyleFor(trend, tc) {
  if (trend === "above") {
    return {
      color: "#FFB340",
      bg: "rgba(255, 179, 64, 0.10)",
      border: "rgba(255, 179, 64, 0.25)",
      icon: TrendingUp,
      label: "Above normal driving",
    };
  }
  if (trend === "below") {
    return {
      color: "#34D17A",
      bg: "rgba(52, 209, 122, 0.10)",
      border: "rgba(52, 209, 122, 0.25)",
      icon: TrendingDown,
      label: "Below normal driving",
    };
  }
  if (trend === "in_line") {
    return {
      // "in line" should read as primary text in both modes — white in dark,
      // dark in light. Falls back to white if tc is undefined.
      color: (tc && tc["text-primary"]) || "#FFFFFF",
      bg: "rgba(127, 127, 127, 0.08)",
      border: "rgba(127, 127, 127, 0.20)",
      icon: Activity,
      label: "In line with historical behavior",
    };
  }
  return {
    color: (tc && tc["text-muted"]) || "#8A8A8E",
    bg: "rgba(138, 138, 142, 0.10)",
    border: "rgba(138, 138, 142, 0.25)",
    icon: Activity,
    label: "Building history",
  };
}

function MultiLineTooltip({ active, payload, label, unit, vehicles, data }) {
  if (!active || !payload?.length) return null;
  const u = unitLabel(unit);
  // `label` here is the `day` number (XAxis dataKey). Resolve it back to the
  // human-readable date label baked into the row (e.g., "May 14").
  const row = (data || []).find((r) => r.day === label);
  const dateLabel = row?.label || `Day ${label}`;
  return (
    <div className="bg-[var(--bg-input)] border border-[var(--border-subtle)] px-3 py-2" style={{ borderRadius: 2 }}>
      <div className="label-tiny mb-1">{dateLabel}</div>
      <div className="space-y-0.5">
        {payload.map((p) => {
          const v = vehicles.find((x) => x.id === p.dataKey);
          if (!v) return null;
          const c = colorForVehicle(v.id, vehicles);
          return (
            <div key={p.dataKey} className="flex items-center gap-1.5 text-[11px] font-mono-data">
              <span
                className="inline-block w-1.5 h-1.5"
                style={{ background: c.fill, borderRadius: 1 }}
              />
              <span className="text-[var(--text-secondary)] truncate max-w-[120px]">{v.name}:</span>
              <span className="text-[var(--text-primary)]">{fmt(p.value || 0, 0)} {u}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DrivingTrendCard({
  stats,
  projection,
  yearlyLimitKm,
  unit,
  isMultiAll = false,
  morphCapable = false,
  vehicles = [],
  perVehicleStats = {},
}) {
  const u = unitLabel(unit);
  const multi = isMultiAll && vehicles.length > 1;
  const tc = useThemeColors();

  // Shared month-window math (uses aggregate stats; identical between modes).
  const monthUsedKm = stats?.month_used_km || 0;
  const monthLabel = stats?.month_label || "";
  const monthStart = stats?.month_start;
  const monthEnd = stats?.month_end;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let daysInMonth = 30;
  let dayOfMonth = today.getDate();
  if (monthStart && monthEnd) {
    const s = new Date(monthStart + "T00:00:00");
    const e = new Date(monthEnd + "T00:00:00");
    daysInMonth = Math.round((e - s) / 86400000) + 1;
    dayOfMonth = Math.min(daysInMonth, Math.max(1, Math.round((today - s) / 86400000) + 1));
  }
  const monthDailyRate = dayOfMonth > 0 ? monthUsedKm / dayOfMonth : 0;
  const projectedMonthEndKm = monthDailyRate * daysInMonth;

  // ─────────────────────────────────────────────────────────────────────
  // Per-vehicle classification (multi mode badge + chart inputs).
  // ─────────────────────────────────────────────────────────────────────
  const perVehicleClass = vehicles.map((v) => ({
    vehicle: v,
    ...classifyVehicle({
      vStats: perVehicleStats[v.id],
      dayOfMonth,
      daysInMonth,
    }),
  }));
  const aboveVehicles = perVehicleClass.filter((p) => p.trend === "above");
  const belowVehicles = perVehicleClass.filter((p) => p.trend === "below");

  // Multi-mode badge: prefer the alert-worthy signal. "Above" always wins
  // over "below" because exceeding the historical baseline is the actionable
  // signal — if any single vehicle is above-normal we surface that with its
  // own color dot. Below-normal is only surfaced when no above signal exists.
  let multiTrend = "in_line";
  let multiBadgeLabel = "In line with historical behavior";
  let multiBadgeVehicle = null;
  if (multi) {
    if (aboveVehicles.length === 1) {
      multiTrend = "above";
      multiBadgeVehicle = aboveVehicles[0].vehicle;
      multiBadgeLabel = `Above normal: ${multiBadgeVehicle.name}`;
    } else if (aboveVehicles.length >= 2) {
      multiTrend = "above";
      multiBadgeLabel = `Above normal across ${aboveVehicles.length} vehicles`;
    } else if (belowVehicles.length === 1) {
      multiTrend = "below";
      multiBadgeVehicle = belowVehicles[0].vehicle;
      multiBadgeLabel = `Below normal: ${multiBadgeVehicle.name}`;
    } else if (belowVehicles.length >= 2) {
      multiTrend = "below";
      multiBadgeLabel = `Below normal across ${belowVehicles.length} vehicles`;
    } else if (perVehicleClass.every((p) => p.trend === "unknown")) {
      multiTrend = "unknown";
      multiBadgeLabel = "Building history";
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Single-mode classification (aggregate stats).
  // ─────────────────────────────────────────────────────────────────────
  const currentMonthKey =
    monthStart ? monthStart.slice(0, 7) : new Date().toISOString().slice(0, 7);
  const pastMonthsAgg = (stats?.monthly || []).filter(
    (m) => m.month && m.month < currentMonthKey && (m.distance_km || 0) > 0,
  );
  const historicalAvgKm =
    pastMonthsAgg.length > 0
      ? pastMonthsAgg.reduce((acc, m) => acc + (m.distance_km || 0), 0) /
        pastMonthsAgg.length
      : 0;
  let singleTrend = "unknown";
  let singleDeltaPct = null;
  if (historicalAvgKm > 0) {
    singleDeltaPct =
      ((projectedMonthEndKm - historicalAvgKm) / historicalAvgKm) * 100;
    if (projectedMonthEndKm > historicalAvgKm * 1.15) singleTrend = "above";
    else if (projectedMonthEndKm < historicalAvgKm * 0.85) singleTrend = "below";
    else singleTrend = "in_line";
  }

  // Resolve the active style (multi vs single) for the headline badge.
  const trend = multi ? multiTrend : singleTrend;
  const style = trendStyleFor(trend, tc);
  const TrendIcon = style.icon;
  const badgeLabel = multi ? multiBadgeLabel : style.label;

  // ─────────────────────────────────────────────────────────────────────
  // Year-end impact narrative — uses aggregate projection in both modes.
  // ─────────────────────────────────────────────────────────────────────
  const projectionKm = projection?.km || 0;
  const limit = yearlyLimitKm || 0;
  const overCap = !!projection?.overCap;
  const deltaToLimitKm = limit > 0 ? Math.abs(projectionKm - limit) : 0;
  let impactHeadline = "—";
  let impactSub = "";
  if (limit > 0 && projectionKm > 0) {
    if (overCap) {
      impactHeadline = `${fmt(fromKm(deltaToLimitKm, unit), 0)} ${u} over your allowance`;
      impactSub =
        trend === "above"
          ? "Heavier-than-usual driving this month is pushing you over."
          : trend === "below"
          ? "Even at this lower pace, projected year-end is still over."
          : "If this pace continues, you'll exceed your annual allowance.";
    } else {
      impactHeadline = `${fmt(fromKm(deltaToLimitKm, unit), 0)} ${u} under your allowance`;
      impactSub =
        trend === "above"
          ? "Above-normal driving narrows your margin — keep an eye on it."
          : trend === "below"
          ? "Lighter month — your margin is growing."
          : "On a typical path — projection is comfortable.";
    }
  } else if (projectionKm > 0) {
    impactHeadline = `${fmt(fromKm(projectionKm, unit), 0)} ${u} year-end`;
    impactSub = "Set an annual allowance on your vehicle to track headroom.";
  }

  // ─────────────────────────────────────────────────────────────────────
  // Middle column data — single mode uses comparison bars + sparkline;
  // multi mode uses a per-vehicle cumulative LineChart.
  // ─────────────────────────────────────────────────────────────────────
  // Strict daily calendar timeline — always zero-filled so the X-axis spans
  // every day of the active month, regardless of whether trips exist for it.
  // Returns rows ordered day-1 through last-day-of-month.
  function buildDailyTimeline(monthStartIso, monthEndIso, dailyKmByDate) {
    const rows = [];
    if (!monthStartIso || !monthEndIso) return rows;
    const s = new Date(monthStartIso + "T00:00:00");
    const e = new Date(monthEndIso + "T00:00:00");
    const todayIso = today.toISOString().slice(0, 10);
    let day = 1;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1), day++) {
      const iso = d.toISOString().slice(0, 10);
      const label = new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      rows.push({
        date: iso,
        day,
        label,
        value: fromKm(dailyKmByDate[iso] || 0, unit),
        isToday: iso === todayIso,
      });
    }
    return rows;
  }

  // X-axis ticks every 5 days, with day 1 always pinned and the final day
  // appended if it's not already a multiple of 5 (so 28-day Feb shows
  // [1, 5, 10, 15, 20, 25, 28] and 31-day months show [1, 5, 10, 15, 20, 25, 30]).
  function buildEveryFiveTicks(daysInMonth) {
    const ticks = [1];
    for (let d = 5; d <= daysInMonth; d += 5) ticks.push(d);
    if (ticks[ticks.length - 1] !== daysInMonth) {
      // include last day only if it adds clarity (>= 2 days from prior tick)
      if (daysInMonth - ticks[ticks.length - 1] >= 2) ticks.push(daysInMonth);
    }
    return ticks;
  }
  const dayTicks = buildEveryFiveTicks(daysInMonth);

  // SINGLE: zero-filled daily timeline + comparison bars.
  const dailyMap = {};
  (stats?.daily_month || []).forEach((d) => (dailyMap[d.date] = d.distance_km));
  const sparkline = buildDailyTimeline(monthStart, monthEnd, dailyMap);
  const compareMax = Math.max(projectedMonthEndKm, historicalAvgKm, 1);
  const currentBarPct = (projectedMonthEndKm / compareMax) * 100;
  const avgBarPct = (historicalAvgKm / compareMax) * 100;

  // MULTI: cumulative km per vehicle per day of current month.
  // MULTI: zero-filled cumulative km per vehicle per day. Future days are
  // injected as nulls so the lines stop at today but the X-axis still spans
  // the full strict daily calendar.
  const multiLineData = [];
  if (multi && monthStart && monthEnd) {
    const s = new Date(monthStart + "T00:00:00");
    const e = new Date(monthEnd + "T00:00:00");
    const todayIso = today.toISOString().slice(0, 10);
    const vDailyMaps = {};
    for (const v of vehicles) {
      const m = {};
      (perVehicleStats[v.id]?.daily_month || []).forEach((d) => {
        m[d.date] = d.distance_km || 0;
      });
      vDailyMaps[v.id] = m;
    }
    const running = Object.fromEntries(vehicles.map((v) => [v.id, 0]));
    let day = 1;
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1), day++) {
      const iso = d.toISOString().slice(0, 10);
      const isFuture = iso > todayIso;
      const label = new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const row = { day, date: iso, label, isFuture };
      for (const v of vehicles) {
        if (!isFuture) running[v.id] += vDailyMaps[v.id][iso] || 0;
        row[v.id] = isFuture ? null : fromKm(running[v.id], unit);
      }
      multiLineData.push(row);
    }
  }
  // Combined household 12-mo average baseline — horizontal reference line.
  const householdAvgKm = perVehicleClass.reduce(
    (acc, p) => acc + (p.historicalAvgKm || 0),
    0,
  );
  const householdAvgDisp = fromKm(householdAvgKm, unit);

  // Pin a min-height when morph-capable so the card frame stays stable.
  const stableStyle = morphCapable
    ? { borderRadius: 4, minHeight: 320 }
    : { borderRadius: 4 };

  return (
    <div
      className="relative bg-[var(--bg-canvas)] border border-[var(--border-hover)] overflow-hidden card-hover"
      style={stableStyle}
      data-testid="driving-trend-card"
      data-mode={multi ? "multi" : "single"}
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ background: style.color, opacity: trend === "unknown" ? 0.3 : 0.85 }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 p-5 lg:p-6 pl-7 lg:pl-8">
        {/* Left — Headline */}
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="label-tiny">Driving Trend</span>
            <span className="text-[10px] tracking-wider text-[var(--text-muted)]">·</span>
            <span className="text-[10px] tracking-wider uppercase text-[var(--text-muted)]">
              {monthLabel}
            </span>
          </div>

          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-display text-4xl lg:text-5xl font-light tracking-tighter text-[var(--text-primary)]">
              {fmt(fromKm(monthUsedKm, unit), 0)}
            </span>
            <span className="text-sm text-[var(--text-muted)] font-mono-data">{u}</span>
          </div>
          <div className="text-xs text-[var(--text-secondary)] font-mono-data">
            so far · Day {dayOfMonth}/{daysInMonth}
          </div>

          <div
            className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 text-[11px] font-mono-data tracking-wider border"
            style={{
              borderRadius: 2,
              color: style.color,
              backgroundColor: style.bg,
              borderColor: style.border,
            }}
            data-testid="driving-trend-status-badge"
          >
            <TrendIcon className="w-3 h-3" strokeWidth={2} />
            {multiBadgeVehicle && (
              <span
                className="inline-block w-1.5 h-1.5 -mr-0.5"
                style={{
                  background: colorForVehicle(multiBadgeVehicle.id, vehicles).fill,
                  borderRadius: 1,
                }}
                data-testid={`driving-trend-badge-dot-${multiBadgeVehicle.id}`}
              />
            )}
            {badgeLabel}
          </div>

          {projectedMonthEndKm > 0 && (
            <div
              className="mt-2 flex items-center gap-1 text-[10px] tracking-wider uppercase"
              data-testid="driving-trend-projected-month-end"
              title={`At your current pace of ${fmt(fromKm(monthDailyRate, unit), 1)} ${u}/day`}
            >
              <span className="text-[var(--text-muted)]">Projected month-end</span>
              <span className="font-mono-data text-[var(--text-primary)]">
                {fmt(fromKm(projectedMonthEndKm, unit), 0)} {u}
              </span>
            </div>
          )}
        </div>

        {/* Middle — multi-line chart in multi mode, comparison bars otherwise */}
        <div className="lg:col-span-5 flex flex-col justify-center">
          {multi ? (
            <div data-testid="driving-trend-multi-chart">
              <div className="flex items-center justify-between mb-2">
                <span className="label-tiny">Current Month · Cumulative</span>
                <span className="text-[10px] tracking-wider uppercase text-[var(--text-muted)] font-mono-data">
                  12-mo avg · {fmt(householdAvgDisp, 0)} {u}
                </span>
              </div>
              <div className="h-[140px] -mx-1">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={multiLineData} margin={{ top: 4, right: 10, left: 4, bottom: 0 }}>
                    <XAxis
                      dataKey="day"
                      type="number"
                      domain={[1, daysInMonth]}
                      stroke={tc["border-strong"]}
                      tick={{ fill: tc["text-muted"], fontSize: 10, fontFamily: "JetBrains Mono" }}
                      tickLine={false}
                      axisLine={{ stroke: tc["border-subtle"] }}
                      ticks={dayTicks}
                      interval={0}
                      allowDecimals={false}
                    />
                    <YAxis
                      width={36}
                      stroke={tc["border-strong"]}
                      tick={{ fill: tc["text-muted"], fontSize: 10, fontFamily: "JetBrains Mono" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      cursor={{ stroke: "#1a1a1a", strokeWidth: 1 }}
                      content={(props) => (
                        <MultiLineTooltip
                          {...props}
                          unit={unit}
                          vehicles={vehicles}
                          data={multiLineData}
                        />
                      )}
                    />
                    {householdAvgDisp > 0 && (
                      <ReferenceLine
                        y={householdAvgDisp}
                        stroke={tc["text-faint"]}
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        ifOverflow="extendDomain"
                        label={{
                          value: "12-MO AVG",
                          position: "right",
                          fill: tc["text-muted"],
                          fontSize: 9,
                          fontFamily: "JetBrains Mono",
                        }}
                      />
                    )}
                    {vehicles.map((v) => {
                      const c = colorForVehicle(v.id, vehicles);
                      return (
                        <Line
                          key={v.id}
                          type="monotone"
                          dataKey={v.id}
                          stroke={c.fill}
                          strokeWidth={1.75}
                          dot={false}
                          activeDot={{ r: 3, fill: c.fill, stroke: c.fill }}
                          isAnimationActive={true}
                          animationDuration={700}
                          connectNulls={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Per-vehicle delta strip */}
              <div className="mt-2 grid grid-cols-2 gap-2">
                {perVehicleClass.map(({ vehicle, deltaPct: dp, trend: t }) => {
                  const c = colorForVehicle(vehicle.id, vehicles);
                  const ts = trendStyleFor(t, tc);
                  return (
                    <div
                      key={vehicle.id}
                      className="flex items-center justify-between gap-2"
                      data-testid={`driving-trend-vehicle-row-${vehicle.id}`}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="inline-block w-1.5 h-1.5 shrink-0"
                          style={{ background: c.fill, borderRadius: 1 }}
                        />
                        <span className="text-[10px] tracking-tight text-[var(--text-primary)] truncate">
                          {vehicle.name}
                        </span>
                      </div>
                      <span
                        className="text-[10px] font-mono-data"
                        style={{ color: ts.color }}
                      >
                        {dp == null ? "—" : `${dp > 0 ? "+" : ""}${dp.toFixed(0)}%`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="label-tiny">vs Your Average</span>
                {singleDeltaPct !== null && (
                  <span
                    className="text-xs font-mono-data"
                    style={{ color: style.color }}
                    data-testid="driving-trend-delta-pct"
                  >
                    {singleDeltaPct > 0 ? "+" : ""}
                    {singleDeltaPct.toFixed(0)}%
                  </span>
                )}
              </div>
              {historicalAvgKm > 0 ? (
                <div className="space-y-2.5">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] tracking-wider uppercase text-[var(--text-secondary)]">
                        This Month (proj.)
                      </span>
                      <span className="text-[11px] font-mono-data text-[var(--text-primary)]">
                        {fmt(fromKm(projectedMonthEndKm, unit), 0)} {u}
                      </span>
                    </div>
                    <div
                      className="relative h-2 bg-[var(--bg-emphasis)] overflow-hidden"
                      style={{ borderRadius: 1 }}
                    >
                      <div
                        className="h-full transition-all duration-700 ease-out bar-grow"
                        style={{
                          width: `${Math.min(100, currentBarPct)}%`,
                          background: style.color,
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] tracking-wider uppercase text-[var(--text-secondary)]">
                        {pastMonthsAgg.length}-mo Average
                      </span>
                      <span className="text-[11px] font-mono-data text-[var(--text-secondary)]">
                        {fmt(fromKm(historicalAvgKm, unit), 0)} {u}
                      </span>
                    </div>
                    <div
                      className="relative h-2 bg-[var(--bg-emphasis)] overflow-hidden"
                      style={{ borderRadius: 1 }}
                    >
                      <div
                        className="h-full transition-all duration-700 ease-out bar-grow"
                        style={{
                          width: `${Math.min(100, avgBarPct)}%`,
                          background: "var(--border-strong)",
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-[11px] text-[var(--text-muted)] py-2">
                  Not enough history yet — log at least one full month to surface a baseline.
                </div>
              )}
              {sparkline.length > 0 && (
                <div className="mt-4" data-testid="driving-trend-daily-chart">
                  {/* Header — explain what this chart is so the bars never
                      read as abstract decoration. */}
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div className="flex items-baseline gap-2">
                      <span className="label-tiny">Daily Distance</span>
                      <span className="text-[10px] tracking-wider uppercase text-[var(--text-muted)]">
                        {monthLabel}
                      </span>
                    </div>
                    <span className="text-[10px] tracking-wider uppercase text-[var(--text-muted)] font-mono-data flex items-center gap-1">
                      <span
                        className="inline-block w-1.5 h-1.5"
                        style={{ background: "#E82127", borderRadius: 1 }}
                      />
                      Today
                    </span>
                  </div>
                  <div className="h-20 -mx-1">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={sparkline}
                        margin={{ top: 2, right: 4, left: 0, bottom: 2 }}
                        barCategoryGap="15%"
                      >
                        <XAxis
                          dataKey="day"
                          type="number"
                          domain={[1, daysInMonth]}
                          stroke={tc["border-strong"]}
                          tick={{
                            fill: tc["text-muted"],
                            fontSize: 9,
                            fontFamily: "JetBrains Mono",
                          }}
                          tickLine={false}
                          axisLine={{ stroke: tc["border-subtle"] }}
                          ticks={dayTicks}
                          interval={0}
                          allowDecimals={false}
                          height={14}
                          tickFormatter={(d) => `${d}`}
                        />
                        <Tooltip
                          cursor={{ fill: "rgba(255,255,255,0.06)" }}
                          content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const p = payload[0].payload;
                            return (
                              <div
                                className="bg-[var(--bg-input)] border border-[var(--border-subtle)] px-2.5 py-1.5"
                                style={{ borderRadius: 2 }}
                              >
                                <div className="label-tiny mb-0.5">
                                  {p.label} {p.isToday && "· today"}
                                </div>
                                <div className="font-mono-data text-xs text-[var(--text-primary)]">
                                  {fmt(p.value, 1)}{" "}
                                  <span className="text-[var(--text-muted)]">{u}</span>
                                </div>
                              </div>
                            );
                          }}
                        />
                        <Bar
                          dataKey="value"
                          radius={[1, 1, 0, 0]}
                          isAnimationActive={false}
                        >
                          {sparkline.map((entry) => (
                            <Cell
                              key={entry.date}
                              fill={entry.isToday ? "#E82127" : tc["bg-inverse"]}
                              fillOpacity={entry.isToday ? 1 : 0.85}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] mt-1 leading-tight">
                    Each bar is one day's driving. Hover for the date.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right — Year-end impact (aggregate, identical between modes) */}
        <div className="lg:col-span-3 lg:border-l lg:border-[var(--border-hover)] lg:pl-6">
          <div className="label-tiny mb-1">Year-End Impact</div>
          <div
            className={`font-mono-data text-xl leading-tight ${
              overCap ? "text-[#FF5A5F]" : "text-[var(--text-primary)]"
            }`}
            data-testid="driving-trend-year-end-impact"
          >
            {impactHeadline}
          </div>
          <div className="text-[11px] text-[var(--text-secondary)] mt-2 leading-snug">{impactSub}</div>
        </div>
      </div>
    </div>
  );
}
